/**
 * CTRL_08_ConfigPanel - Human Oversight & Review Gates Configuration
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Button, Collapse, Select, Tag, Alert, Spin } from 'antd';
import {
  SettingOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option } = Select;

function CTRL_08_ConfigPanel({ projectId, controlCode, project }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeKeys, setActiveKeys] = useState(['1']);

  useEffect(() => {
    if (projectId && controlCode) {
      fetchConfig();
    }
  }, [projectId, controlCode]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getControlConfig(projectId, controlCode);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
      setConfig({
        enabled: false,
        config: {
          require_approval_for: ['high_risk_models', 'model_deployment'],
          approval_workflow: 'single_approver',
          log_reviewer_identity: true
        }
      });
      if (error.response && error.response.status !== 404) {
        showError('Failed to load configuration. Using defaults.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveControlConfig(projectId, controlCode, config);
      showSuccess('Configuration saved successfully');
      setHasChanges(false);
      fetchConfig();
    } catch (error) {
      console.error('Failed to save config:', error);
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig({
      ...config,
      config: {
        ...config.config,
        [field]: value
      }
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!config) return null;

  const configData = config.config || {};
  const isConfigured = config.enabled && configData.require_approval_for?.length > 0;
  const statusIcon = isConfigured ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <WarningOutlined style={{ color: '#faad14' }} />;
  const statusText = isConfigured ? 'Configured' : 'Setup Required';

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SettingOutlined />
          <span>CTRL_08 Configuration: Human Oversight & Review Gates</span>
          {statusIcon}
          <Tag color={isConfigured ? 'green' : 'orange'}>{statusText}</Tag>
        </div>
      }
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanges}
        >
          Save Changes
        </Button>
      }
    >
      <Collapse activeKey={activeKeys} onChange={setActiveKeys} expandIconPosition="right">
        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <strong>1. Enable Human Oversight</strong>
              {config.enabled && <Tag color="green">Active</Tag>}
            </div>
          }
          key="1"
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <Switch
                checked={config.enabled}
                onChange={(checked) => setConfig({ ...config, enabled: checked })}
                size="large"
              />
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  {config.enabled ? 'Oversight Enabled' : 'Oversight Disabled'}
                </div>
                <div style={{ color: '#888', fontSize: '13px' }}>
                  {config.enabled
                    ? 'Monitoring high-risk operations for required approvals'
                    : 'Enable to require human review gates for critical operations'}
                </div>
              </div>
            </div>

            {!config.enabled && (
              <Alert
                type="info"
                message="About Human Oversight"
                description="This control ensures critical AI/ML operations require human approval before execution. Configure which operations need review and the approval workflow."
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </div>
        </Panel>

        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <strong>2. Advanced Configuration</strong>
              {configData.approval_workflow && <Tag color="blue">{configData.approval_workflow}</Tag>}
            </div>
          }
          key="2"
          disabled={!config.enabled}
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                Require Approval For
              </label>
              <Select
                mode="multiple"
                value={configData.require_approval_for || []}
                onChange={(value) => updateConfig('require_approval_for', value)}
                style={{ width: '100%', marginBottom: '8px' }}
                placeholder="Select operations requiring approval"
              >
                <Option value="high_risk_models">High-Risk Models</Option>
                <Option value="model_deployment">Model Deployment</Option>
                <Option value="high_risk_predictions">High-Risk Predictions</Option>
                <Option value="training_runs">Training Runs</Option>
              </Select>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Select which operations require human approval
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                Approval Workflow
              </label>
              <Select
                value={configData.approval_workflow || 'single_approver'}
                onChange={(value) => updateConfig('approval_workflow', value)}
                style={{ width: '100%', marginBottom: '8px' }}
              >
                <Option value="single_approver">Single Approver</Option>
                <Option value="dual_control">Dual Control (2 approvers)</Option>
                <Option value="committee">Committee (3+ approvers, majority)</Option>
                <Option value="custom">Custom Workflow</Option>
              </Select>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Choose approval workflow type
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Switch
                  checked={configData.log_reviewer_identity ?? true}
                  onChange={(checked) => updateConfig('log_reviewer_identity', checked)}
                />
                <label style={{ fontWeight: 'bold' }}>Log Reviewer Identity</label>
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', marginLeft: '44px' }}>
                Record user ID and timestamp for all reviewers
              </div>
            </div>

            <Alert
              type="success"
              message="Configuration Summary"
              description={
                <div>
                  <p><strong>Approval Required:</strong> {configData.require_approval_for?.join(', ') || 'None'}</p>
                  <p><strong>Workflow:</strong> {configData.approval_workflow || 'single_approver'}</p>
                  <p><strong>Log Identity:</strong> {configData.log_reviewer_identity ? 'Yes' : 'No'}</p>
                </div>
              }
              showIcon
              style={{ marginTop: '16px' }}
            />
          </div>
        </Panel>
      </Collapse>
    </Card>
  );
}

export default CTRL_08_ConfigPanel;
